// The conversation model behind the AI Assistant page, kept pure so tests can
// check it without a DOM. The conversation lives in component state only;
// nothing here touches localStorage or sessionStorage, because a question can
// be about a customer or a registrant.
import type { ChatMessage, ChatRequest } from "@dashboard-intelligent/api/src/modules/assistant/types";

/** How many prior turns travel with a question. The server caps again. */
export const HISTORY_LIMIT = 10;

export type NoticeTone = "warning" | "danger";

export type Entry =
  | {
      kind: "message";
      key: string;
      message: ChatMessage;
      /** A user question counts as history only once an answer came back. */
      answered: boolean;
    }
  | { kind: "notice"; key: string; tone: NoticeTone; title: string; body: string };

/** Prior turns, oldest first, as the API wants them: role and content only, the last ten. */
export function historyFrom(entries: Entry[]): ChatRequest["history"] {
  const turns: ChatRequest["history"] = [];
  for (const entry of entries) {
    if (entry.kind !== "message") continue;
    if (entry.message.role === "user" && !entry.answered) continue;
    turns.push({ role: entry.message.role, content: entry.message.content });
  }
  return turns.slice(-HISTORY_LIMIT);
}

/** A wait in words: "30 detik", "2 menit". Unknown waits read "beberapa saat". */
export function waitText(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return "beberapa saat";
  if (seconds < 60) return `${Math.ceil(seconds)} detik`;
  return `${Math.ceil(seconds / 60)} menit`;
}

const clock = new Intl.DateTimeFormat("id-ID", { hour: "2-digit", minute: "2-digit" });

/** "09.42" in the reader's time zone, or null for a timestamp that does not parse. */
export function timeText(iso: string): string | null {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : clock.format(date);
}

/** The line under a bubble: the time, and for an offline answer, which mode wrote it. */
export function metaFor(message: ChatMessage): string | undefined {
  const time = timeText(message.at);
  if (message.role === "assistant" && message.offline) {
    return time ? `${time}, jawaban mode offline` : "Jawaban mode offline";
  }
  return time ?? undefined;
}

export type Block = { kind: "p"; lines: string[] } | { kind: "ul"; items: string[] };

const BULLET = /^\s*[-*]\s+/;

/** Plain text into paragraphs and bullet lists. Blank lines split paragraphs. */
export function blocksOf(content: string): Block[] {
  const blocks: Block[] = [];
  for (const chunk of content.split(/\n\s*\n/)) {
    const lines = chunk
      .split("\n")
      .map((line) => line.trimEnd())
      .filter((line) => line.trim() !== "");
    if (lines.length === 0) continue;
    if (lines.every((line) => BULLET.test(line))) {
      blocks.push({ kind: "ul", items: lines.map((line) => line.replace(BULLET, "")) });
    } else {
      blocks.push({ kind: "p", lines });
    }
  }
  return blocks;
}
